import axios from 'axios';
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type FormEvent,
  type KeyboardEvent,
  type ClipboardEvent,
  type ChangeEvent,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faEye,
  faEyeSlash,
  faWrench,
  faArrowLeft,
  faCheck,
  faXmark,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { authService } from '@/services/auth.service';
import { passwordResetService, type RequestOtpResponse } from '@/services/password-reset.service';
import { useAuthStore } from '@/stores/auth.store';
import { useAccessRestrictionStore } from '@/stores/access-restriction.store';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';
import { MathCaptcha } from '@/features/customers/components/MathCaptcha';
import { RecoverySessionCounter } from '@/components/shared/RecoverySessionCounter';

// ── Types ─────────────────────────────────────────────────────────────────────

type Screen = 'login' | 'forgot-password' | 'verify-otp' | 'reset-password' | 'success';

interface ResetState {
  resetEmail: string;
  maskedEmail: string;
  cooldownUntil: Date | null;
  otpExpiresAt: Date | null;
  resetToken: string;
  /**
   * D-143 — requests made from THIS browser, as counted by the server before it
   * looked the address up. Not the account's OTP count, which may never reach an
   * anonymous caller; that number is disclosed only inside the OTP email.
   */
  sessionRequestsUsed: number;
  sessionRequestLimit: number;
}

interface PolicyCheck {
  label: string;
  met: boolean;
}

// ── Policy helpers ────────────────────────────────────────────────────────────

function checkPolicy(password: string): PolicyCheck[] {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter (A–Z)', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter (a–z)', met: /[a-z]/.test(password) },
    { label: 'One number (0–9)', met: /[0-9]/.test(password) },
    { label: 'One special character (!@#$%…)', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

// ── Timer hook ────────────────────────────────────────────────────────────────

function useCountdown(target: Date | null): number {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!target) {
      setRemaining(0);
      return;
    }
    const tick = () => {
      const ms = target.getTime() - Date.now();
      setRemaining(Math.max(0, Math.ceil(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [target]);

  return remaining;
}

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── OTP Input ─────────────────────────────────────────────────────────────────

interface OtpInputProps {
  value: string[];
  onChange: (digits: string[]) => void;
  disabled?: boolean;
  shake?: boolean;
}

function OtpInput({ value, onChange, disabled = false, shake = false }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const setRef = (i: number) => (el: HTMLInputElement | null) => {
    refs.current[i] = el;
  };

  const handleChange = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...value];
    next[i] = char;
    onChange(next);
    if (char && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[i]) {
        const next = [...value];
        next[i] = '';
        onChange(next);
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      refs.current[i + 1]?.focus();
    } else if (e.key === 'Delete') {
      const next = [...value];
      next[i] = '';
      onChange(next);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    const next = [...value];
    for (let j = 0; j < text.length; j++) {
      next[j] = text[j] ?? '';
    }
    onChange(next);
    const focusIdx = Math.min(text.length, 5);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div
      className={`flex gap-2 justify-center ${shake ? 'animate-[shake_0.4s_ease-in-out]' : ''}`}
      style={shake ? { animation: 'shake 0.4s ease-in-out' } : undefined}
    >
      <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }`}</style>
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={setRef(i)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={value[i] ?? ''}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-11 h-12 text-center text-xl font-semibold rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          aria-label={`OTP digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

// ── Screen: Forgot Password ───────────────────────────────────────────────────

interface ForgotPasswordScreenProps {
  onBack: () => void;
  onSuccess: (email: string, res: RequestOtpResponse) => void;
  direction: 'left' | 'right';
}

function ForgotPasswordScreen({ onBack, onSuccess, direction }: ForgotPasswordScreenProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [devError, setDevError] = useState('');
  const [loading, setLoading] = useState(false);
  // Mirrors the login screen: revealed only after the API reports the address
  // is shared by more than one active account.
  const [needsSecondary, setNeedsSecondary] = useState(false);
  const [secondaryIdentifier, setSecondaryIdentifier] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (needsSecondary && !secondaryIdentifier.trim()) {
      setError('Please enter your username or mobile number');
      return;
    }
    setError('');
    setDevError('');
    setLoading(true);
    try {
      const res = await passwordResetService.requestOtp(
        trimmed,
        needsSecondary ? secondaryIdentifier.trim() : undefined,
      );
      onSuccess(trimmed, res);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errBody = err.response?.data as
          { error?: { code?: string; devCode?: string; devMessage?: string } } | undefined;
        const code = errBody?.error?.code;
        if (import.meta.env.DEV && errBody?.error?.devMessage) {
          const tag = errBody.error.devCode ? `[${errBody.error.devCode}]` : '';
          setDevError(`${tag} ${errBody.error.devMessage}`.trim());
        }
        if (code === 'ADDITIONAL_IDENTIFICATION_REQUIRED') {
          // Shared mailbox — ask who is asking, without revealing anything
          // about the other accounts behind the address.
          setNeedsSecondary(true);
          setError('Additional identification is required.');
        } else if (code === 'DAILY_LIMIT_EXCEEDED') {
          setError("You've reached today's maximum of 5 OTP requests. Please try again tomorrow.");
        } else if (code === 'RESEND_COOLDOWN') {
          setError('Please wait before requesting another OTP.');
        } else if (code === 'EMAIL_DELIVERY_FAILED') {
          setError("We couldn't send the OTP. Please try again.");
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={direction === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}>
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        Back to Login
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground">Forgot your password?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter your email address and we'll send you a one-time code.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormField label="Email Address" htmlFor="reset-email" required error={error}>
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (needsSecondary) {
                setNeedsSecondary(false);
                setSecondaryIdentifier('');
              }
            }}
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            disabled={loading}
          />
        </FormField>

        {needsSecondary && (
          <FormField label="Username or Mobile Number" htmlFor="reset-secondary" required>
            <Input
              id="reset-secondary"
              type="text"
              value={secondaryIdentifier}
              onChange={(e) => setSecondaryIdentifier(e.target.value)}
              placeholder="e.g. jsmith or 9876543210"
              autoComplete="username"
              disabled={loading}
              autoFocus
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Additional identification is required to continue.
            </p>
          </FormField>
        )}

        {import.meta.env.DEV && devError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-700/40 dark:bg-amber-900/10">
            <p className="font-mono text-[11px] text-amber-700 dark:text-amber-400">
              <span className="font-semibold">DEV:</span> {devError}
            </p>
          </div>
        )}

        <Button type="submit" className="w-full min-h-[44px]" disabled={loading}>
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            'Send OTP'
          )}
        </Button>
      </form>
    </div>
  );
}

// ── Screen: Verify OTP ────────────────────────────────────────────────────────

interface VerifyOtpScreenProps {
  resetState: ResetState;
  onBack: () => void;
  onSuccess: (resetToken: string) => void;
  onResend: (res: RequestOtpResponse) => void;
  direction: 'left' | 'right';
}

function VerifyOtpScreen({
  resetState,
  onBack,
  onSuccess,
  onResend,
  direction,
}: VerifyOtpScreenProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [dailyLimitHit, setDailyLimitHit] = useState(false);
  /**
   * D-143 rule 4 — a UX state, NOT enforcement. Disabling the control stops this
   * browser from firing pointless requests once its own counter is spent; it grants
   * and denies nothing. The server still decides, still counts per account over the
   * UTC day, and still refuses on its own terms if the user clears storage or moves
   * to another browser.
   */
  const sessionCeilingReached = resetState.sessionRequestsUsed >= resetState.sessionRequestLimit;

  const expiryRemaining = useCountdown(resetState.otpExpiresAt);
  const cooldownRemaining = useCountdown(resetState.cooldownUntil);

  const isExpired = expiryRemaining === 0 && resetState.otpExpiresAt !== null;
  const canVerify = digits.every((d) => d !== '') && !isExpired && !loading;

  // Auto-submit when all 6 digits filled
  useEffect(() => {
    if (digits.every((d) => d !== '') && !loading && !isExpired) {
      void handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const res = await passwordResetService.verifyOtp(resetState.resetEmail, code);
      onSuccess(res.resetToken);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errData = err.response?.data as
          { error?: { code?: string; attemptsRemaining?: number } } | undefined;
        const code = errData?.error?.code;
        const remaining = errData?.error?.attemptsRemaining;
        if (code === 'OTP_LOCKED') {
          setError(
            'This code is locked due to too many incorrect attempts. Please request a new code.',
          );
        } else if (code === 'OTP_EXPIRED' || code === 'OTP_NOT_FOUND') {
          setError('This code has expired. Please request a new one.');
        } else if (code === 'OTP_INVALID') {
          const msg =
            remaining !== undefined && remaining > 0
              ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
              : 'Incorrect code.';
          setError(msg);
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
      setShake(true);
      setDigits(Array(6).fill(''));
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldownRemaining > 0 || resendLoading || dailyLimitHit || sessionCeilingReached) return;
    setResendLoading(true);
    setError('');
    try {
      const res = await passwordResetService.requestOtp(resetState.resetEmail);
      setDigits(Array(6).fill(''));
      onResend(res);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const code = (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
        if (code === 'DAILY_LIMIT_EXCEEDED') {
          setDailyLimitHit(true);
          setError("You've reached today's maximum OTP requests. Please try again tomorrow.");
        } else {
          setError('Could not resend. Please try again.');
        }
      } else {
        setError('Could not resend. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={direction === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}>
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        Change email
      </button>

      {/* D-142 — this copy must not claim an email was sent. The server returns the
          same neutral response whether the address matched an account, the daily cap
          was reached, the 60-second cooldown was active, or delivery failed (D-141
          rules 2-4), so asserting delivery here was the one place the product
          over-promised. The limits below are POLICY — constant for every caller, and
          therefore disclosing nothing about any account (D-142 rules 2-3). This
          string is identical for every caller and must not branch on any server
          signal. */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground">Enter verification code</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          If an account exists for{' '}
          <span className="font-medium text-foreground">{resetState.maskedEmail}</span>, a 6-digit
          code is on its way.
        </p>
        {/* D-143 — the browser-scoped counter REPLACES D-142's fixed policy
            sentence, and keeps its safety content: the component still says a
            missing email may mean a limit was reached. It says "requests from this
            browser", never "OTPs sent" — the server increments it before it looks
            the address up, so it reads the same for a real account and an unknown
            one. The account's true count is disclosed only inside the OTP email. */}
        <RecoverySessionCounter
          id="recovery-session-counter-help"
          used={resetState.sessionRequestsUsed}
          limit={resetState.sessionRequestLimit}
          className="mt-3 text-xs"
          mutedClassName="text-muted-foreground"
          dangerClassName="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-destructive"
        />
      </div>

      <div className="space-y-5">
        <OtpInput
          value={digits}
          onChange={setDigits}
          disabled={loading || isExpired}
          shake={shake}
        />

        {error && <p className="text-center text-sm text-destructive">{error}</p>}

        {/* Dual countdowns */}
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground mb-1">OTP expires in</p>
            <p
              className={`text-lg font-mono font-semibold ${isExpired ? 'text-destructive' : 'text-foreground'}`}
            >
              {isExpired ? 'Expired' : formatMmSs(expiryRemaining)}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground mb-1">Resend in</p>
            <p className="text-lg font-mono font-semibold text-foreground">
              {cooldownRemaining > 0 ? formatMmSs(cooldownRemaining) : '—'}
            </p>
          </div>
        </div>

        {/* D-141 — the "N of 5 remaining today" counter was REMOVED. It came from
            account-specific quota fields the API no longer returns: a real account
            could never show 5 of 5 while an unknown address always did, so the
            counter itself disclosed account existence (audit L-1). The daily limit
            is unchanged and still enforced server-side. Do not reintroduce a
            per-account quota display here. */}

        <Button
          type="button"
          className="w-full min-h-[44px]"
          disabled={!canVerify}
          onClick={() => {
            void handleVerify();
          }}
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify OTP'
          )}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              void handleResend();
            }}
            disabled={
              cooldownRemaining > 0 || resendLoading || dailyLimitHit || sessionCeilingReached
            }
            aria-describedby="recovery-session-counter-help"
            className="text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40"
          >
            {resendLoading ? 'Resending...' : 'Resend code'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Screen: Reset Password ────────────────────────────────────────────────────

interface ResetPasswordScreenProps {
  resetToken: string;
  onSuccess: () => void;
  direction: 'left' | 'right';
}

function ResetPasswordScreen({ resetToken, onSuccess, direction }: ResetPasswordScreenProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [apiError, setApiError] = useState('');

  const policy = checkPolicy(newPassword);
  const allMet = policy.every((p) => p.met);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setConfirmError('');
    setApiError('');

    if (newPassword !== confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }
    if (!allMet) return;

    setLoading(true);
    try {
      await passwordResetService.resetPassword(resetToken, newPassword, confirmPassword);
      onSuccess();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const code = (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
        if (code === 'RESET_TOKEN_INVALID' || code === 'RESET_TOKEN_EXPIRED') {
          setApiError('Your reset session has expired. Please restart the password reset process.');
        } else if (code === 'PASSWORD_POLICY_VIOLATION') {
          setApiError('Password does not meet the requirements. Please check the rules below.');
        } else {
          setApiError('Something went wrong. Please try again.');
        }
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={direction === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground">Reset your password</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Create a strong new password for your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormField label="New Password" htmlFor="new-password" required>
          <div className="relative">
            <Input
              id="new-password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              autoFocus
              disabled={loading}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
              aria-label={showNew ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </FormField>

        <FormField
          label="Confirm Password"
          htmlFor="confirm-password"
          required
          error={confirmError}
        >
          <div className="relative">
            <Input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmError('');
              }}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={loading}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </FormField>

        {/* Live policy indicators */}
        {newPassword.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">Password requirements:</p>
            {policy.map((p) => (
              <div key={p.label} className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={p.met ? faCheck : faXmark}
                  className={`h-3 w-3 shrink-0 ${p.met ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}
                />
                <span className={`text-xs ${p.met ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {p.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {apiError && <p className="text-sm text-destructive">{apiError}</p>}

        <Button
          type="submit"
          className="w-full min-h-[44px]"
          disabled={loading || !allMet || !newPassword || !confirmPassword}
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
              Resetting...
            </>
          ) : (
            'Reset Password'
          )}
        </Button>
      </form>
    </div>
  );
}

// ── Screen: Password Changed ──────────────────────────────────────────────────

interface PasswordChangedScreenProps {
  onBackToLogin: () => void;
}

function PasswordChangedScreen({ onBackToLogin }: PasswordChangedScreenProps) {
  return (
    <div className="animate-slide-in-right text-center">
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
          <FontAwesomeIcon
            icon={faShieldHalved}
            className="h-8 w-8 text-green-600 dark:text-green-400"
          />
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-foreground mb-2">Password Updated</h2>
      <p className="text-sm text-muted-foreground mb-1">
        Your password has been changed successfully.
      </p>
      <p className="text-sm text-muted-foreground mb-8">
        All active sessions have been signed out.
        <br />
        Please sign in with your new password.
      </p>

      <Button onClick={onBackToLogin} className="w-full min-h-[44px]">
        Back to Login
      </Button>
    </div>
  );
}

// ── LoginPage (root component) ────────────────────────────────────────────────

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);

  // Login state
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  // Shared-email disambiguation. Revealed only when the API answers 409; the
  // client never tries to predict whether an address is shared.
  const [needsSecondary, setNeedsSecondary] = useState(false);
  const [secondaryIdentifier, setSecondaryIdentifier] = useState('');
  // D-099 — captcha, revealed only when the server says so (403 CAPTCHA_REQUIRED).
  // The client never guesses the threshold; showing it pre-emptively would leak
  // how many failures an identifier has accumulated.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  // Bumped after every failed attempt so the challenge is re-fetched: a token is
  // consumed server-side on verification, so reusing one would always fail.
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Screen state
  const [screen, setScreen] = useState<Screen>('login');
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  // Password reset shared state
  const [resetState, setResetState] = useState<ResetState>({
    resetEmail: '',
    maskedEmail: '',
    cooldownUntil: null,
    otpExpiresAt: null,
    resetToken: '',
    sessionRequestsUsed: 0,
    sessionRequestLimit: 5,
  });

  const go = useCallback((to: Screen, dir: 'left' | 'right' = 'right') => {
    setDirection(dir);
    setScreen(to);
  }, []);

  // ── Login handlers ──────────────────────────────────────────────────────────

  const validate = () => {
    const e: Record<string, string> = {};
    if (!identifier.trim()) e.identifier = 'Please enter your email, username, or mobile number';
    if (!password) e.password = 'Please enter your password';
    // Only required once the server has told us the email is ambiguous.
    if (needsSecondary && !secondaryIdentifier.trim()) {
      e.secondaryIdentifier = 'Please enter your username or mobile number';
    }
    if (captchaRequired && !captchaAnswer.trim()) {
      e.captchaAnswer = 'Please complete the security check';
    }
    return e;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const res = await authService.login(
        identifier.trim(),
        password,
        needsSecondary ? secondaryIdentifier.trim() : undefined,
        captchaRequired ? { token: captchaToken, answer: captchaAnswer.trim() } : undefined,
      );
      // A fresh sign-in starts from a clean presentation state. Without this a
      // restriction flag left over from a previous session in this same tab
      // would bounce the user to /access-restricted before their first request
      // had been made — and would do so even if the workspace had since been
      // reactivated. Clearing it is safe: only the backend decides access, and
      // the next guarded call re-establishes the truth.
      useAccessRestrictionStore.getState().clear();
      setAuth(res.user, res.accessToken, res.refreshToken);
      const redirect = searchParams.get('redirect');
      navigate(redirect ? decodeURIComponent(redirect) : ROUTES.ROOT, { replace: true });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const code = axios.isAxiosError(err)
        ? (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code
        : undefined;

      if (status === 409 && code === 'ADDITIONAL_IDENTIFICATION_REQUIRED') {
        // The address is shared. Reveal the extra field in place — the email and
        // password the user already typed are deliberately left untouched so
        // they only add what is missing. No page reload, no re-entry.
        setNeedsSecondary(true);
        // Neutral wording: never state how many accounts share the address, or
        // anything about who they are.
        toast.info('Additional identification is required.');
      } else if (status === 401) {
        // Identical message whether the password was wrong, the second factor
        // was wrong, or no such account exists — the UI must not become the
        // enumeration oracle that the API deliberately avoids being.
        toast.error(
          "We couldn't find an account matching those details. Please check and try again.",
        );
      } else if (status === 403 && code === 'CAPTCHA_REQUIRED') {
        // D-099. Reveal the challenge and let the user retry in place — nothing
        // they typed is cleared. This 403 says only "solve this first"; it is
        // raised before the account is even looked up, so it confirms nothing
        // about whether the identifier exists.
        setCaptchaRequired(true);
        setCaptchaAnswer('');
        setCaptchaNonce((n) => n + 1);
        toast.info('Please complete the security check and try again.');
      } else if (status === 403) {
        // C-3 lockout. Without this branch a locked-out user saw the generic
        // "something went wrong" and simply kept retrying, never learning that
        // further attempts are pointless for the next few minutes.
        //
        // Safe to say plainly: the lockout is keyed on the submitted identifier
        // whether or not an account exists behind it, so a 403 confirms nothing
        // the 401 above already withholds.
        toast.error('Too many failed sign-in attempts. Please try again in a few minutes.');
      } else if (status === 503) {
        // Sign-in protection is unavailable and the server is refusing rather
        // than admitting anyone unprotected. Retrying shortly is the correct
        // advice; the cause stays server-side.
        toast.error('Sign-in is temporarily unavailable. Please try again shortly.');
      } else {
        toast.error('Something went wrong. Please try again in a moment.');
      }

      // A captcha token is consumed server-side on verification, whether or not
      // the answer was right, so any failed attempt must fetch a fresh one.
      if (captchaRequired) {
        setCaptchaAnswer('');
        setCaptchaNonce((n) => n + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Password reset handlers ─────────────────────────────────────────────────

  const handleForgotPassword = () => go('forgot-password', 'right');

  const handleOtpRequestSuccess = (email: string, res: RequestOtpResponse) => {
    setResetState({
      resetEmail: email,
      maskedEmail: res.maskedEmail,
      cooldownUntil: new Date(Date.now() + res.cooldownSeconds * 1000),
      otpExpiresAt: new Date(res.otpExpiresAt),
      resetToken: '',
      sessionRequestsUsed: res.sessionRequestsUsed,
      sessionRequestLimit: res.sessionRequestLimit,
    });
    go('verify-otp', 'right');
  };

  const handleOtpVerified = (resetToken: string) => {
    setResetState((prev) => ({ ...prev, resetToken }));
    go('reset-password', 'right');
  };

  const handleResend = (res: RequestOtpResponse) => {
    setResetState((prev) => ({
      ...prev,
      maskedEmail: res.maskedEmail,
      cooldownUntil: new Date(Date.now() + res.cooldownSeconds * 1000),
      otpExpiresAt: new Date(res.otpExpiresAt),
      sessionRequestsUsed: res.sessionRequestsUsed,
      sessionRequestLimit: res.sessionRequestLimit,
    }));
  };

  const handlePasswordChanged = () => go('success', 'right');

  const handleBackToLogin = () => {
    setResetState({
      resetEmail: '',
      maskedEmail: '',
      cooldownUntil: null,
      otpExpiresAt: null,
      resetToken: '',
      // The COUNTER is not cleared here in any meaningful sense: the server owns
      // it, keyed by the stored session id, so the next request returns the real
      // running total regardless of what this component remembers.
      sessionRequestsUsed: 0,
      sessionRequestLimit: 5,
    });
    go('login', 'left');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Mobile-only logo */}
      <div className="mb-8 flex flex-col items-center gap-3 lg:hidden">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-xl ring-2 ring-primary/30"
          style={{ background: '#283618' }}
        >
          <FontAwesomeIcon icon={faWrench} className="h-6 w-6 text-white" />
        </div>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-foreground">Service Portal</h1>
          <p className="text-sm text-muted-foreground">Field service management</p>
        </div>
      </div>

      {/* Screen container */}
      <div className="overflow-hidden">
        {screen === 'login' && (
          <div className={direction === 'left' ? 'animate-slide-in-left' : undefined}>
            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground">Welcome back</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Sign in to your workspace to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <FormField
                label="Email, Username or Mobile Number"
                htmlFor="identifier"
                required
                error={errors.identifier}
              >
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    // A different address may not be shared at all, so the
                    // disambiguation state must not carry over.
                    if (needsSecondary) {
                      setNeedsSecondary(false);
                      setSecondaryIdentifier('');
                    }
                  }}
                  placeholder="you@example.com"
                  autoComplete="username"
                  aria-required="true"
                  disabled={loading}
                />
              </FormField>

              <FormField label="Password" htmlFor="password" required error={errors.password}>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    aria-required="true"
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
                  </button>
                </div>
              </FormField>

              {/* Revealed in place when the API reports the email is shared.
                  The hint names no colleague and no count — it says only what
                  the user must do next. */}
              {needsSecondary && (
                <FormField
                  label="Username or Mobile Number"
                  htmlFor="secondaryIdentifier"
                  required
                  error={errors.secondaryIdentifier}
                >
                  <Input
                    id="secondaryIdentifier"
                    type="text"
                    value={secondaryIdentifier}
                    onChange={(e) => setSecondaryIdentifier(e.target.value)}
                    placeholder="e.g. jsmith or 9876543210"
                    autoComplete="username"
                    aria-required="true"
                    aria-describedby="secondaryIdentifier-hint"
                    disabled={loading}
                    autoFocus
                  />
                  <p id="secondaryIdentifier-hint" className="mt-1.5 text-xs text-muted-foreground">
                    Additional identification is required to continue.
                  </p>
                </FormField>
              )}

              {/* D-099 — additional defence layer after repeated failures. It
                  does not replace the lockout: the C-3 counter keeps running
                  underneath and still locks the identifier at its own threshold. */}
              {captchaRequired && (
                <div data-testid="login-captcha">
                  <MathCaptcha
                    key={captchaNonce}
                    queryKey={`login-captcha-${captchaNonce}`}
                    fetchChallenge={authService.publicCaptcha}
                    answer={captchaAnswer}
                    onAnswerChange={setCaptchaAnswer}
                    onTokenChange={setCaptchaToken}
                    disabled={loading}
                  />
                  {errors.captchaAnswer && (
                    <p className="mt-1.5 text-xs text-destructive">{errors.captchaAnswer}</p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-border accent-primary"
                    disabled={loading}
                  />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className="mt-1 w-full min-h-[44px]"
                disabled={loading}
                aria-disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-xs text-muted-foreground">
              Having trouble? Contact your workspace administrator.
            </p>
          </div>
        )}

        {screen === 'forgot-password' && (
          <ForgotPasswordScreen
            direction={direction}
            onBack={() => go('login', 'left')}
            onSuccess={handleOtpRequestSuccess}
          />
        )}

        {screen === 'verify-otp' && (
          <VerifyOtpScreen
            key={resetState.otpExpiresAt?.toISOString()}
            direction={direction}
            resetState={resetState}
            onBack={() => go('forgot-password', 'left')}
            onSuccess={handleOtpVerified}
            onResend={handleResend}
          />
        )}

        {screen === 'reset-password' && (
          <ResetPasswordScreen
            direction={direction}
            resetToken={resetState.resetToken}
            onSuccess={handlePasswordChanged}
          />
        )}

        {screen === 'success' && <PasswordChangedScreen onBackToLogin={handleBackToLogin} />}
      </div>
    </div>
  );
}
